import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { ThemeProvider } from 'styled-components'
import { GlobalStyle } from './styles/global.js'
import { Header } from './components/Header/'
import { Footer } from './components/Footer/'
import { RateLimitModal } from './components/RateLimitModal'
import { initGA, trackPageView, initMetaPixel, trackMetaPixelPageView } from './analytics'
import { useEffect, useMemo, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import { useRateLimit } from './hooks/useRateLimit'
import 'react-toastify/dist/ReactToastify.css'
import { lightTheme, darkTheme } from './styles/theme'
import { RateLimitProvider } from './contexts/RateLimitContext'
import { AuthProvider } from './contexts/AuthContext'

import { Home } from './pages/Home'
import { Login } from './pages/Login'
import { Register } from './pages/Register'
import { ForgotPassword } from './pages/ForgotPassword'
import { ResetPassword } from './pages/ResetPassword'
import { VerifyEmail } from './pages/VerifyEmail'
import { ResendVerification } from './pages/ResendVerification'
import { Pricing } from './pages/Pricing'
import { CheckoutSuccess } from './pages/CheckoutSuccess'
import { ConcreteIndex } from './pages/concrete/Index'
import { ConcreteBending } from './pages/concrete/Bending'
import { ConcreteBendingTension } from './pages/concrete/BendingTension'
import { ConcreteShear } from './pages/concrete/Shear'
import { ConcreteTorsion } from './pages/concrete/Torsion'
import { ConcreteCantilever } from './pages/concrete/Cantilever'
import { ConcreteWallFoundation } from './pages/concrete/WallFoundation/index.js'
import { ConcreteIsolatedFooting } from './pages/concrete/IsolatedFooting'
import { ConcreteBransonInertia } from './pages/concrete/BransonInertia'
import { ConcreteRebarFatigue } from './pages/concrete/RebarFatigue'
import { ConcreteShearRebarFatigue } from './pages/concrete/ShearRebarFatigue'
import { ConcreteStairLongitudinalLanding } from './pages/concrete/StairLongitudinalLanding'
import { ConcreteBendingMomentByRebar } from './pages/concrete/BendingMomentByRebar'
import { ConcreteAnchorage } from './pages/concrete/Anchorage'
import { ConcreteRebarLayer } from './pages/concrete/RebarLayer'
import { ConcreteObliqueFlexCompression } from './pages/concrete/ObliqueFlexCompression'
import { ConcreteColumnDesign } from './pages/concrete/ColumnDesign'
import { ConcretePortico2D } from './pages/concrete/Portico2D'
import { ConcreteGrelha } from './pages/concrete/Grelha'
import { ElectricalIndex } from './pages/electrical/Index'
import { ElectricalCircuits } from './pages/electrical/Circuits'
import { ElectricalConduit } from './pages/electrical/Conduit'
import { ElectricalVoltageDrop } from './pages/electrical/VoltageDrop'
import { MansoryIndex } from './pages/mansory/Index'
import { MansoryCompressive } from './pages/mansory/Compressive'
import { MansoryBending } from './pages/mansory/Bending/index.js'
import { MansoryPanelBending } from './pages/mansory/PanelBending/index.js'
import { MansoryShear } from './pages/mansory/Shear/index.js'
import { PlumbingIndex } from './pages/plumbing/Index'
import { PlumbingSepticTank } from './pages/plumbing/SepticTank'
import { PlumbingWaterTank } from './pages/plumbing/WaterTank'
import { PlumbingPluvial } from './pages/plumbing/Pluvial'
import { GeotechnicalIndex } from './pages/geotechnical/Index'
import { GeotechnicalPile } from './pages/geotechnical/Pile'
import { GeotechnicalFlexuralWallStability } from './pages/geotechnical/FlexuralWallStability'
import { SteelIndex } from './pages/steel/Index/index.js'
import { SteelWeldNormal } from './pages/steel/Weld/index.js'
import { SteelPinnedBasePlates } from './pages/steel/PinnedBasePlates/index.js'
import { SteelBending } from './pages/steel/Bending'
import { SteelCompressive } from './pages/steel/Compressive'
import { SteelFloorPlate } from './pages/steel/FloorPlate/index.js'
import { SteelCircularTubeBending } from './pages/steel/CircularTubeBending/index.js'
import { SteelWindLoads } from './pages/steel/WindLoads/index.js'
import { CFSteelCompressive } from './pages/cfSteel/Compressive/index.js'
import { CFSteelBending } from './pages/cfSteel/Bending/index.js'
import { PageSeoManager } from './components/PageSeoManager'
import { MansoryCheckWall } from './pages/mansory/CheckWall/index.js'
import { NotFound } from './pages/NotFound'
import { ProtectedRoute } from './components/ProtectedRoute'
import { AdminRoute } from './components/AdminRoute'
import { Admin } from './pages/Admin'

const RouteChangeTracker = () => {
  const location = useLocation()

  useEffect(() => {
    window.scrollTo(0, 0)
    initGA()
    initMetaPixel()
    trackPageView(location.pathname)
    trackMetaPixelPageView()
  }, [location])

  return null
}

const RateLimitModalWrapper = () => {
  const { showUpgradeModal, closeUpgradeModal, rateLimit } = useRateLimit()
  return <RateLimitModal isOpen={showUpgradeModal} onClose={closeUpgradeModal} resetAt={rateLimit.resetAt} />
}

export function App() {
  const [themeName, setThemeName] = useState(() => {
    const storedTheme = localStorage.getItem('theme')
    if (storedTheme === 'light' || storedTheme === 'dark') return storedTheme
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    return prefersDark ? 'dark' : 'light'
  })

  const theme = useMemo(() => (themeName === 'dark' ? darkTheme : lightTheme), [themeName])

  const handleToggleTheme = () => {
    setThemeName((prev) => (prev === 'dark' ? 'light' : 'dark'))
  }

  useEffect(() => {
    localStorage.setItem('theme', themeName)
    document.documentElement.setAttribute('data-theme', themeName)
  }, [themeName])

  return (
    <ThemeProvider theme={theme}>
      <AuthProvider>
        <RateLimitProvider>
          <Router>
            <RouteChangeTracker />
            <RateLimitModalWrapper />
            <PageSeoManager />
            <GlobalStyle />
            <Header themeName={themeName} onToggleTheme={handleToggleTheme} />
            <ToastContainer theme={themeName === 'dark' ? 'dark' : 'light'} />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/resend-verification" element={<ResendVerification />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route
                path="/checkout/success"
                element={
                  <ProtectedRoute>
                    <CheckoutSuccess />
                  </ProtectedRoute>
                }
              />
              <Route path="/concreto" element={<ConcreteIndex />} />
              <Route
                path="/concreto/flexao"
                element={
                  <ProtectedRoute>
                    <ConcreteBending />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/flexo-tracao"
                element={
                  <ProtectedRoute>
                    <ConcreteBendingTension />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/cisalhamento"
                element={
                  <ProtectedRoute>
                    <ConcreteShear />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/torcao"
                element={
                  <ProtectedRoute>
                    <ConcreteTorsion />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/consolo"
                element={
                  <ProtectedRoute>
                    <ConcreteCantilever />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/fundacao-parede"
                element={
                  <ProtectedRoute>
                    <ConcreteWallFoundation />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/sapata-isolada"
                element={
                  <ProtectedRoute>
                    <ConcreteIsolatedFooting />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/inercia-equivalente"
                element={
                  <ProtectedRoute>
                    <ConcreteBransonInertia />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/fadiga-armadura-longitudinal"
                element={
                  <ProtectedRoute>
                    <ConcreteRebarFatigue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/fadiga-armadura-transversal"
                element={
                  <ProtectedRoute>
                    <ConcreteShearRebarFatigue />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/escada-longitudinal-patamar"
                element={
                  <ProtectedRoute>
                    <ConcreteStairLongitudinalLanding />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/momento-area"
                element={
                  <ProtectedRoute>
                    <ConcreteBendingMomentByRebar />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/ancoragem"
                element={
                  <ProtectedRoute>
                    <ConcreteAnchorage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/flexo-compressao-obliqua"
                element={
                  <ProtectedRoute>
                    <ConcreteObliqueFlexCompression />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/pilares"
                element={
                  <ProtectedRoute>
                    <ConcreteColumnDesign />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/camada-armadura"
                element={
                  <ProtectedRoute>
                    <ConcreteRebarLayer />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/portico-2d"
                element={
                  <ProtectedRoute>
                    <ConcretePortico2D />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/concreto/grelha"
                element={
                  <ProtectedRoute>
                    <ConcreteGrelha />
                  </ProtectedRoute>
                }
              />
              <Route path="/eletrica" element={<ElectricalIndex />} />
              <Route
                path="/eletrica/circuitos"
                element={
                  <ProtectedRoute>
                    <ElectricalCircuits />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/eletrica/eletroduto"
                element={
                  <ProtectedRoute>
                    <ElectricalConduit />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/eletrica/queda-de-tensao"
                element={
                  <ProtectedRoute>
                    <ElectricalVoltageDrop />
                  </ProtectedRoute>
                }
              />
              <Route path="/alvenaria" element={<MansoryIndex />} />
              <Route
                path="/alvenaria/compressao"
                element={
                  <ProtectedRoute>
                    <MansoryCompressive />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alvenaria/flexao"
                element={
                  <ProtectedRoute>
                    <MansoryBending />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alvenaria/painel"
                element={
                  <ProtectedRoute>
                    <MansoryPanelBending />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alvenaria/cisalhamento"
                element={
                  <ProtectedRoute>
                    <MansoryShear />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/alvenaria/parede"
                element={
                  <ProtectedRoute>
                    <MansoryCheckWall />
                  </ProtectedRoute>
                }
              />
              <Route path="/aco" element={<SteelIndex />} />
              <Route
                path="/aco/solda-axial"
                element={
                  <ProtectedRoute>
                    <SteelWeldNormal />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aco/base-rotulada"
                element={
                  <ProtectedRoute>
                    <SteelPinnedBasePlates />
                  </ProtectedRoute>
                }
              />
              <Route
                path="aco/laminado-flexao"
                element={
                  <ProtectedRoute>
                    <SteelBending />
                  </ProtectedRoute>
                }
              />
              <Route
                path="aco/laminado-compressao"
                element={
                  <ProtectedRoute>
                    <SteelCompressive />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aco/compressao-frio"
                element={
                  <ProtectedRoute>
                    <CFSteelCompressive />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aco/flexao-frio"
                element={
                  <ProtectedRoute>
                    <CFSteelBending />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aco/chapa"
                element={
                  <ProtectedRoute>
                    <SteelFloorPlate />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aco/tubo-circular-flexao"
                element={
                  <ProtectedRoute>
                    <SteelCircularTubeBending />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/aco/cargas-vento"
                element={
                  <ProtectedRoute>
                    <SteelWindLoads />
                  </ProtectedRoute>
                }
              />
              <Route path="/geotecnia" element={<GeotechnicalIndex />} />
              <Route
                path="/geotecnia/estaca"
                element={
                  <ProtectedRoute>
                    <GeotechnicalPile />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/geotecnia/muro-flexao"
                element={
                  <ProtectedRoute>
                    <GeotechnicalFlexuralWallStability />
                  </ProtectedRoute>
                }
              />
              <Route path="/hidrossanitario" element={<PlumbingIndex />} />
              <Route
                path="/hidrossanitario/fossa"
                element={
                  <ProtectedRoute>
                    <PlumbingSepticTank />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hidrossanitario/reservatorio"
                element={
                  <ProtectedRoute>
                    <PlumbingWaterTank />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/hidrossanitario/pluvial"
                element={
                  <ProtectedRoute>
                    <PlumbingPluvial />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <AdminRoute>
                    <Admin />
                  </AdminRoute>
                }
              />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <Footer />
          </Router>
        </RateLimitProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
